import { useState, useMemo } from 'react';
import { HouseholdMember, Schedule, mapHouseholdAggregateAndChoreAggregateToChoreWithDetails, formatSchedule, formatDueText, elapsedFraction, isChildIn } from '../domain';
import { useAppStore } from '../stores';

export interface EditFormData {
  name: string;
  description: string;
  labels: string[];
  schedule: Schedule;
  assignments: { userId: string; isResponsible: boolean }[];
}

export const useChoreRow = (householdId: string, choreId: string) => {
  // All hooks must be called unconditionally before any early returns.
  const { user } = useAppStore(state => state.auth);
  const { markChoreDone, deleteChore, updateChore, loading, mutating, households } = useAppStore(
    state => state.chores
  );

  const household = households.find(h => h.household.id === householdId);
  const choreAgg = household?.chores.find(c => c.chore.id === choreId);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showBackdatePicker, setShowBackdatePicker] = useState(false);
  const [backdateValue, setBackdateValue] = useState('');
  const [editFormData, setEditFormData] = useState<EditFormData>({
    name: '',
    description: '',
    labels: [],
    schedule: { type: 'minutes', interval: 1440 },
    assignments: [],
  });

  // useMemo prevents a new object reference on every render, keeping
  // derived state stable when unrelated store updates occur.
  const chore = useMemo(
    () =>
      household && choreAgg
        ? mapHouseholdAggregateAndChoreAggregateToChoreWithDetails(household, choreAgg)
        : null,
    [household, choreAgg]
  );

  // Early return comes after all hooks.
  if (!chore) {
    return { state: null, actions: null, loading, mutating };
  }

  const householdMembers: HouseholdMember[] = household?.household.member_ids.map((id, i) => ({
    id,
    name: household?.household.member_display_names.at(i) || '',
  })) ?? [];

  const nameOf = (userId: string) => householdMembers.find(m => m.id === userId)?.name || 'Former member';
  // The primary first, so the row's avatar and the details list lead with them.
  const assignees = [...chore.assigned_to]
    .sort((a, b) => Number(b.isResponsible) - Number(a.isResponsible))
    .map(a => ({ id: a.userId, name: nameOf(a.userId), isPrimary: a.isResponsible }));

  const scheduleDescription = formatSchedule(chore.schedule);

  const lastCompletedByName = householdMembers.find(m => m.id === chore.last_completed_by)?.name;
  const lastCompletedText = chore.last_completed_at
    ? `${new Date(chore.last_completed_at).toLocaleDateString()}${lastCompletedByName ? ` by ${lastCompletedByName}` : ''}`
    : null;

  const handleMarkDone = () => user && markChoreDone(chore.household_id, chore.id, user.uid);

  const openBackdatePicker = () => {
    setBackdateValue(new Date().toISOString().split('T')[0]);
    setShowBackdatePicker(true);
  };

  const handleBackdateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !backdateValue) return;
    // Combine with the current time-of-day (and no 'Z' suffix) so the string is parsed
    // as local time instead of UTC midnight, avoiding an off-by-one-day shift.
    const timeOfDay = new Date().toTimeString().split(' ')[0];
    const completedAt = new Date(`${backdateValue}T${timeOfDay}`).toISOString();
    markChoreDone(chore.household_id, chore.id, user.uid, completedAt);
    setShowBackdatePicker(false);
  };

  const handleDelete = async () => {
    await deleteChore(chore.household_id, chore.id);
    setShowDeleteConfirm(false);
  };

  // Populate form data at the moment the modal opens, not via an effect.
  const openEditModal = () => {
    setEditFormData({
      name: chore.name,
      description: chore.description || '',
      labels: chore.labels ?? [],
      schedule: chore.schedule,
      assignments: chore.assigned_to.map(a => ({
        userId: a.userId,
        isResponsible: a.isResponsible,
      })),
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFormData.name.trim()) {
      setEditError('Chore name is required');
      return;
    }
    setEditError(null);
    await updateChore(
      chore.household_id,
      chore.id,
      editFormData.name,
      editFormData.description,
      editFormData.schedule,
      editFormData.assignments,
      editFormData.labels
    );
    setShowEditModal(false);
  };

  const updateField = (field: keyof EditFormData, value: EditFormData[typeof field]) =>
    setEditFormData(prev => ({ ...prev, [field]: value }));

  const toggleAssignment = (userId: string) => {
    setEditFormData(prev => {
      const exists = prev.assignments.find(a => a.userId === userId);
      return {
        ...prev,
        assignments: exists
          ? prev.assignments.filter(a => a.userId !== userId)
          : [...prev.assignments, { userId, isResponsible: false }],
      };
    });
  };

  const toggleResponsible = (userId: string) =>
    setEditFormData(prev => ({
      ...prev,
      assignments: prev.assignments.map(a => (a.userId === userId ? { ...a, isResponsible: !a.isResponsible } : a)),
    }));

  return {
    state: {
      name: chore.name,
      description: chore.description || '',
      labels: chore.labels ?? [],
      scheduleDescription,
      lastCompletedText,
      status: chore.status,
      dueText: formatDueText(chore.daysUntilDue, chore.nextDue),
      nextDueText: chore.nextDue.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'long' }),
      ringProgress: elapsedFraction(chore.status, chore.percentageUntilDue),
      householdName: chore.household.name,
      // Naming the household only helps someone who has more than one.
      showHousehold: households.length > 1,
      assignees,
      user,
      showDeleteConfirm,
      showEditModal,
      editFormData,
      editError,
      householdMembers,
      loading,
      mutating,
      showBackdatePicker,
      backdateValue,
      choreCreatedAt: chore.created_at,
      // A child only marks chores done; editing and deleting are the adults'.
      canEdit: !isChildIn(chore.household, user?.uid),
    },
    actions: {
      setShowDeleteConfirm,
      setShowEditModal,
      openEditModal,
      handleMarkDone,
      handleDelete,
      handleEditSubmit,
      updateField,
      toggleAssignment,
      toggleResponsible,
      openBackdatePicker,
      setShowBackdatePicker,
      setBackdateValue,
      handleBackdateSubmit,
    },
  };
};
